/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import SiteDashboardShell, { useSiteDashboard } from "@/components/customer/SiteDashboardShell";
import { fetchSiteAppointments, decideSiteAppointment, fetchSiteAvailability, patchSiteAvailability } from "@/lib/api";

const FILTERS = ["pending", "confirmed", "upcoming", "all"];

const ACTIONS_BY_STATUS = {
  pending: [["accept", "Accept"], ["decline", "Decline"], ["propose_new_time", "Propose new time"]],
  reschedule_proposed: [["accept", "Accept"], ["decline", "Decline"], ["cancel", "Cancel"]],
  confirmed: [["complete", "Mark completed"], ["cancel", "Cancel"], ["no_show", "No-show"]],
};

function AppointmentRow({ appt, onDecide }) {
  const actions = ACTIONS_BY_STATUS[appt.status] || [];
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E2E8F0] last:border-0 px-4 py-3.5" data-testid="appointment-row">
      <div>
        <p className="text-sm font-medium text-[#0F172A]">{appt.requested_date} {appt.requested_time || ""} — {appt.customer_name}</p>
        <p className="text-xs text-[#94A3B8]">{appt.service || "General appointment"} · <span className="capitalize">{appt.status.replace(/_/g, " ")}</span></p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {actions.map(([action, label]) => (
          <button key={action} onClick={() => onDecide(appt.id, action)}
            className="rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-xs font-medium text-[#0F172A] hover:border-[#10B981]">{label}</button>
        ))}
      </div>
    </div>
  );
}

export default function SiteAgenda() {
  const dashboard = useSiteDashboard();
  const { token, slug } = dashboard;
  const [filter, setFilter] = useState("pending");
  const [appointments, setAppointments] = useState(null);
  const [availability, setAvailability] = useState(null);

  const load = () => {
    if (!token) return;
    const params = filter === "all" ? {} : filter === "upcoming" ? { status: "confirmed" } : { status: filter };
    fetchSiteAppointments(token, slug, params).then(setAppointments).catch(() => {});
    fetchSiteAvailability(token, slug).then(setAvailability).catch(() => {});
  };
  useEffect(load, [token, slug, filter]);

  const onDecide = (id, action) => decideSiteAppointment(token, slug, id, { action }).then(load).catch(() => {});
  const toggleApprovalMode = () => {
    const next = availability?.approval_mode === "automatic" ? "manual" : "automatic";
    patchSiteAvailability(token, slug, { approval_mode: next }).then(setAvailability).catch(() => {});
  };

  return (
    <SiteDashboardShell active="agenda" dashboard={dashboard}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-lg font-bold font-display text-[#0F172A]">Agenda</h2>
        {availability && (
          <button onClick={toggleApprovalMode} className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-xs font-medium text-[#0F172A] hover:border-[#10B981]" data-testid="approval-mode-toggle">
            Appointment approval: <strong className="capitalize">{availability.approval_mode}</strong> (click to switch)
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium capitalize ${filter === f ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569]"}`}>{f}</button>
        ))}
      </div>

      <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
        {!appointments ? (
          <p className="p-6 text-sm text-[#475569]">Loading…</p>
        ) : appointments.items.length === 0 ? (
          <p className="p-6 text-sm text-[#94A3B8]">No appointment requests yet. New requests from your website will appear here.</p>
        ) : (
          appointments.items.map((a) => <AppointmentRow key={a.id} appt={a} onDecide={onDecide} />)
        )}
      </div>
    </SiteDashboardShell>
  );
}
